import type { StatusTrackerStepData } from '@/components/common';
import { OfferStatus } from '@/api/generated/models/offerStatus';
import i18n from '@/i18n';

export const statusConfig: Record<OfferStatus, { labelKey: string; bg: string; fg: string }> = {
  [OfferStatus.DRAFT]: {
    labelKey: 'offers.status.draft',
    bg: 'var(--color-bg-surface-2)',
    fg: 'var(--color-text-secondary)'
  },
  [OfferStatus.APPROVED]: {
    labelKey: 'offers.status.approved',
    bg: 'var(--color-role-user-soft)',
    fg: 'var(--color-role-user)'
  },
  [OfferStatus.SENT]: {
    labelKey: 'offers.status.sent',
    bg: 'var(--color-status-warning-soft)',
    fg: 'var(--color-status-warning)'
  },
  [OfferStatus.ACCEPTED]: {
    labelKey: 'offers.status.accepted',
    bg: 'var(--color-status-success-soft)',
    fg: 'var(--color-status-success)'
  },
  [OfferStatus.REJECTED]: {
    labelKey: 'offers.status.rejected',
    bg: 'var(--color-status-danger-soft)',
    fg: 'var(--color-status-danger)'
  },
  [OfferStatus.EXPIRED]: {
    labelKey: 'offers.status.expired',
    bg: 'var(--color-status-danger-soft)',
    fg: 'var(--color-status-danger)'
  },
  [OfferStatus.CANCELLED]: {
    labelKey: 'offers.status.cancelled',
    bg: 'var(--color-status-danger-soft)',
    fg: 'var(--color-status-danger)'
  }
};

/**
 * Koraci za StatusTracker (Figma node 4859:13767) — vidi taj node za potpunu specifikaciju
 * stanja. Naziv svakog koraka je isti kao naziv statusa (statusConfig labelKey), da se uvijek
 * poklapa s badgeom u headeru. Klijent nikad ne vidi Nacrt (svoja kopija nastaje tek kad se
 * ponuda pošalje), pa partner/klijent gledaju drugačiji početni korak — isto pravilo kao Status
 * Tracker dokumentacija ("View=Klijent počinje od trenutka kad klijentova kopija nastaje").
 * REJECTED/EXPIRED se mogu dogoditi samo NAKON slanja (SENT), pa Nacrt+Poslano ostaju "done",
 * a zadnji korak (Prihvaćena) se ZAMJENJUJE iznimkom — ne dodaje se kao dodatni korak.
 */
export const buildOfferTrackerSteps = (
  status: OfferStatus,
  isPartnerView: boolean
): StatusTrackerStepData[] => {
  const t = i18n.t.bind(i18n);
  const labelFor = (s: OfferStatus) => t(statusConfig[s].labelKey);
  const isException =
    status === OfferStatus.REJECTED ||
    status === OfferStatus.EXPIRED ||
    status === OfferStatus.CANCELLED;

  const steps: StatusTrackerStepData[] = [];
  if (isPartnerView) {
    steps.push({
      key: 'draft',
      label: labelFor(OfferStatus.DRAFT),
      state: status === OfferStatus.DRAFT ? 'current' : 'done'
    });
    steps.push({
      key: 'approved',
      label: labelFor(OfferStatus.APPROVED),
      state:
        status === OfferStatus.DRAFT
          ? 'upcoming'
          : status === OfferStatus.APPROVED
            ? 'current'
            : 'done'
    });
  }
  steps.push({
    key: 'sent',
    label: labelFor(OfferStatus.SENT),
    state:
      status === OfferStatus.DRAFT || status === OfferStatus.APPROVED
        ? 'upcoming'
        : status === OfferStatus.SENT
          ? 'current'
          : 'done'
  });
  steps.push(
    isException
      ? {
          key: 'exception',
          label: labelFor(status),
          state: 'exception'
        }
      : {
          key: 'accepted',
          label: labelFor(OfferStatus.ACCEPTED),
          state: status === OfferStatus.ACCEPTED ? 'current' : 'upcoming'
        }
  );
  return steps;
};
